import React from 'react';
import { Clock, Info, AlertTriangle, CheckCircle, XCircle, ChevronDown } from 'lucide-react';
import { theme } from '../../styles/theme';

interface Event {
  timestamp: string;
  type: 'info' | 'warning' | 'success' | 'error';
  message: string;
  details?: string;
}

interface RecentEventsLogProps {
  events?: Event[];
}

const EVENT_ICONS = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
  error: XCircle,
};

const EVENT_COLORS = {
  info: 'text-interactive-primary',
  warning: 'text-qc-warning',
  success: 'text-qc-success',
  error: 'text-qc-error',
};

export const RecentEventsLog: React.FC<RecentEventsLogProps> = ({ events = [] }) => {
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  if (events.length === 0) return null;

  return (
    <div className="bg-bg-surface border border-border rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-text-secondary" />
        <h3 className={`text-sm font-semibold ${theme.text.primary}`}>
          Recent Events
        </h3>
        <span className={`text-xs ${theme.text.tertiary} ml-auto`}>
          Last {events.length}
        </span>
      </div>

      {/* Events List */}
      <div className="space-y-2">
        {events.map((event, index) => {
          const Icon = EVENT_ICONS[event.type];
          const colorClass = EVENT_COLORS[event.type];
          const isExpanded = expandedIndex === index;

          return (
            <div
              key={index}
              className="border border-border rounded-lg p-3 hover:bg-bg-base transition-colors"
            >
              <div className="flex items-start gap-2">
                <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colorClass}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${theme.text.primary}`}>
                    {event.message}
                  </p>
                  <p className={`text-xs ${theme.text.tertiary} mt-1`}>
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </p>
                </div>
                {event.details && (
                  <button
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    className={`text-text-tertiary hover:text-text-secondary transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Expandable Details */}
              {event.details && isExpanded && (
                <div className={`mt-2 pt-2 border-t border-border text-xs ${theme.text.secondary}`}>
                  {event.details}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
